import React from "react";
import { toast } from "sonner";
import { LogOut, Store, Mail, Calendar } from "lucide-react";
import { useAuth } from "../../../../lib/auth";
import useStorefronts from "@/features/vendor/common/storefronts/useStorefronts";

/**
 * VendorProfilePage — the restaurant owner's account and storefronts.
 */
export default function VendorProfilePage() {
  const { vendor, logout } = useAuth();
  const { storefronts } = useStorefronts();

  const onLogout = () => {
    logout();
    toast.success("Signed out");
  };

  const identity = vendor;
  const roleLabel = "Business Owner";
  const roleTint = "bg-emerald-500/10 border-emerald-500/30 text-emerald-300";

  return (
    <div className="space-y-8">
      {/* Header card */}
      <div className="card-surface p-6" data-testid="vendor-profile-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-brand-soft border border-brand/40 grid place-items-center text-brand font-display text-xl font-medium">
              {identity?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-muted">Signed in as</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-display font-medium tracking-tight" data-testid="profile-name">
                  {identity?.name || "—"}
                </h1>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border font-medium ${roleTint}`} data-testid="profile-role">
                  {roleLabel}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-secondary">
                <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {identity?.email}</span>
                {vendor?.businessName && (
                  <span className="inline-flex items-center gap-1.5"><Store className="h-3.5 w-3.5" /> {vendor.businessName}</span>
                )}
                {identity?.createdAt && (
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Since {new Date(identity.createdAt).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onLogout} className="btn-ghost inline-flex items-center gap-2" data-testid="profile-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        {/* Assigned scope */}
        <div className="mt-6 pt-6 border-t border-line grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-ink-muted mb-2">Your storefronts</div>
            <div className="flex flex-wrap gap-2">
              {storefronts.map((s) => (
                <span key={s.id} className="tag" data-testid={`profile-store-${s.id}`}>
                  <Store className="h-3 w-3" /> {s.name || s.slug}
                </span>
              ))}
              {storefronts.length === 0 && (
                <span className="text-sm text-ink-muted">No storefronts yet.</span>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
